// ============================================================================
// CategoryBreakdownCard (E2, F-523) — repartition d'une section par categorie
// ============================================================================
// Portage du PoC components/finance/category-breakdown-card.tsx : barre horizontale
// empilee + legende (pastille, libelle, part, ecart vs budget, montant).
//
// Les couleurs viennent de `categoryColor` (lib portee) -> `var(--chart-cat-N)`
// (definies dans index.css, BL-12). L'IDENTITE de chaque categorie est portee par sa
// LIGNE DE LEGENDE (libelle + montant + part) : la couleur n'est qu'un rappel — c'est
// ce qui rend la palette lisible malgre la proximite red-500/green-700 en protanopie
// (cf. note palette dans index.css).
// Les depenses arrivent DEJA agregees par groupe (~4 parts, groupExpenseLines) : les
// colorer une a une serait illisible avec ~23 lignes.

import type { TFunction } from 'i18next';
import type { StatementLine } from '../../lib/finance/computations';
import type { FinanceSection } from '../../lib/finance/catalog';
import { categoryColor } from '../../lib/finance/palette';
import { formatCad, formatVariancePct, varianceToneClass } from '../../pages/finance/format';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

export interface CategoryBreakdownCardProps {
  title: string;
  section: FinanceSection;
  lines: StatementLine[];
  total: StatementLine;
  locale: string;
  t: TFunction;
}

export function CategoryBreakdownCard({
  title, section, lines, total, locale, t,
}: CategoryBreakdownCardProps) {
  const sorted = [...lines].sort((a, b) => b.actual - a.actual);
  const positive = sorted.filter((l) => l.actual > 0);
  const sum = positive.reduce((s, l) => s + l.actual, 0);

  return (
    <section className={CARD}>
      <h3 className="text-head font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
        {t('gest:finance.breakdown.total', { amount: formatCad(total.actual, locale) })}
      </p>

      {sum > 0 ? (
        <div
          className="mt-3 flex h-3 overflow-hidden rounded-full"
          role="img"
          aria-label={positive.map((l) => `${l.label} ${formatCad(l.actual, locale)}`).join(', ')}
        >
          {positive.map((l) => (
            // Sepration de 2px entre parts (border-l sur la couleur du fond) : deux
            // teintes voisines ne se touchent jamais.
            <div
              key={l.code}
              className="min-w-1 border-l-2 border-white first:border-l-0 dark:border-slate-800"
              style={{ width: `${(l.actual / sum) * 100}%`, backgroundColor: categoryColor(section, l.code) }}
            />
          ))}
        </div>
      ) : (
        <p className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-meta text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500">
          {t('gest:finance.breakdown.empty')}
        </p>
      )}

      <ul className="mt-3 flex flex-col gap-2">
        {sorted.map((l) => {
          const share = sum > 0 ? l.actual / sum : 0;
          return (
            <li key={l.code} className="flex items-center gap-2 text-field">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: categoryColor(section, l.code) }}
              />
              <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{l.label}</span>
              <span className={`text-meta tabular-nums ${varianceToneClass(l.varianceBudget)}`}>
                {t('gest:finance.breakdown.vsBudget', { pct: formatVariancePct(l.pctVsBudget, locale) })}
              </span>
              <span className="w-24 text-right font-medium tabular-nums text-slate-800 dark:text-slate-100">
                {formatCad(l.actual, locale)}
              </span>
              <span className="w-12 text-right text-meta tabular-nums text-slate-400 dark:text-slate-500">
                {share > 0 ? `${Math.round(share * 100)} %` : '—'}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
